import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, ArrowRightIcon, ClockIcon, MailIcon, MessageSquareIcon, PhoneIcon, TargetIcon, UserIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { SegmentedProgress } from '../ui/SegmentedProgress';
import type { Lead, LeadSource } from '../../types';
import { formatAge, formatDuration, formatRelative } from '../../utils/format';
interface LeadWorkspaceProps {
  lead: Lead;
  source?: LeadSource;
  priorityIndex: number;
  priorityLabel: string;
  priorityReason: string;
  requiredCalls: number;
  ownerName: string;
  readOnly?: boolean;
  onCall?: () => void;
  onSms?: () => void;
  onEmail?: () => void;
  onNext?: () => void;
}
const detailLabel = 'text-[11px] font-semibold uppercase tracking-wide text-ink-400';
const detailValue = 'mt-1 text-sm font-medium text-ink-900';
export function LeadWorkspace({
  lead,
  source,
  priorityIndex,
  priorityLabel,
  priorityReason,
  requiredCalls,
  ownerName,
  readOnly = false,
  onCall,
  onSms,
  onEmail,
  onNext
}: LeadWorkspaceProps) {
  const overdue = lead.activities.filter(a => a.dueInMinutes !== undefined && a.dueInMinutes < 0);
  const nextActivity = lead.activities.find(a => a.dueInMinutes !== undefined && a.dueInMinutes >= 0);
  const callsRemaining = Math.max(0, requiredCalls - lead.callsToday);
  return <Card className="overflow-hidden" data-id="element-197" data-mp="1el4okvwo901r">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-100 bg-brand-50 px-6 py-3.5" data-id="element-198" data-mp="1ei0c7b91yy00">
        <div className="flex items-center gap-3" data-id="element-199" data-mp="1o9mozc11h54v">
          <span className="tabular flex h-7 items-center rounded-lg bg-brand-600 px-2.5 text-xs font-bold text-white" data-id="element-200" data-mp="1obh8clwnwp6u">
            Priority {priorityIndex}
          </span>
          <div data-id="element-201" data-mp="vsk15tzqrn5c">
            <p className="text-sm font-semibold text-brand-900" data-id="element-202" data-mp="vsajqusriob9">{priorityLabel}</p>
            <p className="text-xs text-brand-700/80" data-id="element-203" data-mp="qth8l90y79e9">{priorityReason}</p>
          </div>
        </div>
        <span className="flex items-center gap-1.5 text-xs font-medium text-brand-700" data-id="element-204" data-mp="2qvqk513j7jzb">
          <TargetIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-205" data-mp="2vv32p9nde4w3" />
          Presented by management rules
        </span>
      </div>

      <div className="px-6 pb-6 pt-5" data-id="element-206" data-mp="30n37pgiarnzj">
        <motion.div key={lead.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} data-id="element-207" data-mp="35lek9yzz8clg">
          <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-208" data-mp="2cwz95njyuefe">
            <div data-id="element-209" data-mp="2cz3b3zyb13d1">
              <h1 className="text-2xl font-semibold tracking-tight text-ink-900" data-id="element-210" data-mp="2hw3g07jf402k">{lead.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-211" data-mp="2lxp1gha0pmod">
                <Badge tone="neutral" data-id="element-212" data-mp="2lz04gevn2qef">{lead.status}</Badge>
                <Badge tone="brand" data-id="element-213" data-mp="3tc0f2okz00di">{source?.name ?? 'Unassigned Source'}</Badge>
                <Badge tone="neutral" data-id="element-214" data-mp="176f312j77ch">{formatAge(lead.ageMinutes)}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-ink-200 px-3 py-2" data-id="element-215" data-mp="1lpqli9hosqg6">
              <UserIcon className="h-4 w-4 text-ink-400" aria-hidden="true" data-id="element-216" data-mp="1hndk1x9z3ep7" />
              <div className="leading-tight" data-id="element-217" data-mp="1cx6rxng95c27">
                <p className="text-[11px] text-ink-500" data-id="element-218" data-mp="1cvvpbqyjmaco">Assigned Loan Officer</p>
                <p className="text-sm font-medium text-ink-900" data-id="element-219" data-mp="17yvi3cpyj1jz">{ownerName}</p>
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 rounded-xl border border-ink-200 bg-ink-50/60 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-220" data-mp="130ajtiu9mry6">
            <div data-id="element-221" data-mp="tyb2epfc9lup">
              <p className={detailLabel} data-id="element-222" data-mp="txsvmyv1vp8j">Phone</p>
              <p className={`${detailValue} tabular`} data-id="element-223" data-mp="tykucijy2331">{lead.phone}</p>
            </div>
            <div className="min-w-0" data-id="element-224" data-mp="p0sqcq60yqih">
              <p className={detailLabel} data-id="element-225" data-mp="2ocbyx3nf1wrm">Email</p>
              <p className={`${detailValue} truncate`} data-id="element-226" data-mp="2ob0wp89mcx2m">{lead.email}</p>
            </div>
            <div data-id="element-227" data-mp="2jeahen5n25i9">
              <p className={detailLabel} data-id="element-228" data-mp="2jdsc71100gy7">Last Contact Attempt</p>
              <p className={detailValue} data-id="element-229" data-mp="1g1tq8yi2zul9">
                {lead.lastContactMinutes !== undefined ? formatRelative(lead.lastContactMinutes) : 'No attempts yet'}
              </p>
            </div>
            <div data-id="element-230" data-mp="170vjw9k67azi">
              <p className={detailLabel} data-id="element-231" data-mp="171divdy7w3b4">Next Required Activity</p>
              <p className={detailValue} data-id="element-232" data-mp="1bzoy647a760q">
                {overdue.length > 0 ? overdue[0].title : nextActivity ? `${nextActivity.title} · in ${formatDuration(nextActivity.dueInMinutes ?? 0)}` : callsRemaining > 0 ? `Call attempt ${lead.callsToday + 1} of ${requiredCalls}` : 'Cadence complete'}
              </p>
            </div>
          </div>

          {overdue.length > 0 ? <ul className="mt-4 space-y-2" data-id="element-233" data-mp="eeaq72g82mss">
              {overdue.map(activity => <li key={activity.id} className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-id="element-234" data-mp="jb14plcdpac3">
                  <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" data-id="element-235" data-mp="3qx3segucpn5m" />
                  <div className="min-w-0" data-id="element-236" data-mp="9m410olw73f6">
                    <p className="text-sm font-semibold text-amber-900" data-id="element-237" data-mp="4oc75p1ot78c">
                      Past Due{' '}
                      {activity.type === 'event' ? 'Appointment' : activity.type === 'task' ? 'Task' : 'Activity'}{' '}
                      · {activity.title}
                    </p>
                    <p className="mt-0.5 text-xs text-amber-800" data-id="element-238" data-mp="nflim3v0q62k">
                      {formatDuration(activity.dueInMinutes ?? 0)} past due
                      {activity.detail ? ` · ${activity.detail}` : ''}
                    </p>
                  </div>
                </li>)}
            </ul> : null}

          <div className="mt-5 rounded-xl border border-ink-200 px-5 py-4" data-id="element-239" data-mp="14islihvs98gp">
            <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-240" data-mp="19hdogp2f668u">
              <p className="text-sm font-semibold text-ink-900" data-id="element-241" data-mp="1eevsc11kxv9z">
                Calls Today{' '}
                <span className="tabular text-ink-500" data-id="element-242" data-mp="1j6ne7y9sjcts">
                  {lead.callsToday} of {requiredCalls} Complete
                </span>
              </p>
              <span className="flex items-center gap-1.5 text-xs font-medium text-ink-500" data-id="element-243" data-mp="1na3cez1idl8l">
                <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-244" data-mp="1s8wpwrgspc8r" />
                {callsRemaining > 0 ? `${callsRemaining} more required to clear today's cadence` : 'Cadence satisfied — lead exits the queue'}
              </span>
            </div>
            <SegmentedProgress className="mt-3" completed={lead.callsToday} total={requiredCalls} tone={callsRemaining === 0 ? 'emerald' : 'brand'} data-id="element-245" data-mp="2tqanar1ehtu" />
          </div>

          {!readOnly ? <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-246" data-mp="2ga9spb79vqo6">
              <button type="button" onClick={onCall} className="flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 py-4 text-sm font-semibold text-white shadow-card transition-all hover:bg-brand-700 active:scale-[0.99]" data-id="element-247" data-mp="2c8om27lc08mf">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-248" data-mp="2zysfjm4t2rzp" />
                Call {lead.phone}
              </button>
              <button type="button" onClick={onSms} className="flex items-center justify-center gap-2 rounded-xl border border-ink-200 bg-white px-5 py-4 text-sm font-semibold text-ink-800 transition-all hover:border-ink-300 hover:bg-ink-50 active:scale-[0.99]" data-id="element-249" data-mp="2uyd1escp9ggo">
                <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-250" data-mp="1ejlbcm310laj" />
                Send SMS
              </button>
              <button type="button" onClick={onEmail} className="flex items-center justify-center gap-2 rounded-xl border border-ink-200 bg-white px-5 py-4 text-sm font-semibold text-ink-800 transition-all hover:border-ink-300 hover:bg-ink-50 active:scale-[0.99]" data-id="element-251" data-mp="3eps5myo9zley">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-252" data-mp="17zkl6mgx6zd" />
                Send Email
              </button>
              <button type="button" onClick={onNext} className="flex items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-4 text-sm font-semibold text-white transition-all hover:bg-ink-800 active:scale-[0.99]" data-id="element-253" data-mp="66kkt61pzqnt">
                Next Lead
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-254" data-mp="k6llolhziy3a" />
              </button>
            </div> : null}
        </motion.div>
      </div>
    </Card>;
}